import { LuDownload } from "react-icons/lu";
import Cookies from "js-cookie";

import { Badge } from "@/components/ui/badge/badge";
import { cn } from "@/lib/utils";
import { useViewModal } from "./use-view-modal";
import { Button } from "@/components/ui/button/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { takeActionOnAllowanceRequest } from "@/config/mutation/admin";
import { toast } from "sonner";
import { AxiosError } from "axios";

const classes: {
  [key: string]: string;
} = {
  Approved: `bg-blue-600 text-white font-normal`,
  Pending: `bg-yellow-500 text-black font-normal`,
  Rejected: `bg-red-500 text-white font-normal`,
  Cancelled: `bg-indigo text-white font-normal`,
};

export const ViewAllowanceModal = () => {
  const queryClient = useQueryClient();
  const key = Cookies.get("key") as string;
  const { open, onOpenChange, allowanceRequest } = useViewModal();

  const [takeActionOpen, setTakeActionOpen] = useState(false);

  const [value, setValue] = useState("");

  const { mutate, isPending } = useMutation({
    mutationFn: takeActionOnAllowanceRequest(key),
  });

  const submitAction = async () => {
    if (!value || value === undefined) {
      return toast.error("Please select an action first.");
    }

    mutate(
      {
        allowanceId: allowanceRequest?.id as string,
        status: value,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["allowances"],
          });

          toast.success("Action Taken.");
          onOpenChange(false);
        },

        onError: (error) => {
          console.log("ERROR WHILE CREATING TAKING ACTION ON ALLOWANCE", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            "transition-all duration-300",
            takeActionOpen && "max-w-5xl"
          )}
        >
          <DialogHeader>
            <DialogTitle className="tracking-normal flex items-center gap-3">
              Allowance Request
              <div className="">
                <Button
                  size="sm"
                  onClick={() => setTakeActionOpen((prev) => !prev)}
                >
                  Take Action
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="flex">
            <div className="mt-5 shrink-0 pr-10">
              <div className="flex flex-row items-center gap-2">
                <Badge
                  className={cn(classes[allowanceRequest?.status as string])}
                >
                  {allowanceRequest?.status}
                </Badge>
                <span>•</span>
                <div className="flex flex-row items-center text-sm">
                  <span>
                    {allowanceRequest?.employeeName} (
                    {allowanceRequest?.employeeEmail})
                  </span>
                </div>
              </div>

              <p className="mt-3">{allowanceRequest?.reason}</p>

              {allowanceRequest?.attachment && (
                <div className="flex mt-3">
                  <a
                    href={allowanceRequest?.attachment as string}
                    download="Download Attachment"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-1 font-normal"
                    >
                      <LuDownload /> Attachment
                    </Button>
                  </a>
                </div>
              )}
            </div>

            {takeActionOpen && (
              <div className="w-full border-l px-5 py-2 flex flex-col gap-3">
                <h2 className="text-lg font-medium">
                  Take action on allowance
                </h2>

                <div className="mt-5">
                  <Select
                    value={value}
                    onValueChange={setValue}
                    disabled={isPending}
                  >
                    <span>Choose Action</span>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Action..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Approved">Approved</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-end">
                  <Button onClick={submitAction} disabled={isPending}>
                    Submit
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
